import { CircularProgress } from '@chakra-ui/progress'
import { Box } from '@chakra-ui/react'
import * as React from 'react'

import { useBgColorCtx } from '~theme/BgColorProvider'
import { TColorKey } from '~theme/typings'
import { getColorValue } from '~theme/utils'
import { useTheme } from '~ui/theme/ThemeProvider'

type TChakraProps = Pick<PropsOf<typeof CircularProgress>, 'minHeight'>

type TSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
type TThickness = 'xs' | 'sm' | 'md' | 'lg'

// props common for both components bellow
type TCommonProps = Children &
  TChakraProps & {
    /**
     * size/radius of the circular spinner
     */
    size?: TSize
    /**
     * width of the line
     */
    thickness?: TThickness
  }

// props that might depend on Theme
type TProps = TCommonProps & {
  /**
   * color of the spinning line,
   * key of theme
   */
  color?: TColorKey
}

// ? should not get a color but rather contrast & use fg color based on bg provider?

/**
 * infinite circular loading spinner
 */
export const Spinner: React.FC<TProps> = ({ color = `bgColors.contrasted`, ...props }) => {
  const theme = useTheme()
  const { bgColorValue } = useBgColorCtx()

  const colorValue = getColorValue(theme)(color)

  return <NoThemeSpinner {...props} color={colorValue} bgColor={bgColorValue} />
}

// props not depended on Theme
type TNoThemeProps = TCommonProps & {
  /**
   * color of the spinning line
   */
  color: string
  /**
   * color of the rest of the circle/track
   * where the line is currently not present
   */
  bgColor: string
}

/**
 * infinite circular loading spinner
 * this version can be used even without ThemeProvider above in the component tree
 * because we might need to show the spinner sooner than theme is initialized
 */
export const NoThemeSpinner: React.FC<TNoThemeProps> = ({
  size,
  color,
  bgColor,
  children,
  thickness,
  ...chakraProps
}) => {
  const sizeValue = getSize(size ?? `md`)
  const maxBoxDimension = `calc(${sizeValue}/ 2)`

  return (
    <Box display={`grid`} placeItems={`center`} alignContent={`center`}>
      <CircularProgress
        {...chakraProps}
        capIsRound
        isIndeterminate
        gridRow={1}
        gridColumn={1}
        lineHeight={1} // for vertical alignment with content
        display={`grid`} // for vertical alignment with content
        alignItems={`center`} // for vertical alignment with content
        color={color}
        trackColor={bgColor}
        size={sizeValue}
        thickness={getThickness(thickness ?? `md`)}
      />
      {children && (
        <Box
          gridRow={1}
          gridColumn={1}
          lineHeight={1}
          maxWidth={maxBoxDimension}
          maxHeight={maxBoxDimension}
        >
          {children}
        </Box>
      )}
    </Box>
  )
}

// translate size label to a value
const getSize = (size: TSize) => {
  switch (size) {
    case 'xs':
      return `1rem`
    case 'sm':
      return `2rem`
    case 'md':
      return `3rem`
    case 'lg':
      return `5rem`
    case 'xl':
      return `10rem`
  }
}

// translate thickness label to a value
const getThickness = (thickness: TThickness) => {
  switch (thickness) {
    case 'xs':
      return `2px`
    case 'sm':
      return `5px`
    case 'md':
      return `10px`
    case 'lg':
      return `15px`
  }
}
